import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {Button, EMPHASIS, FlexColumn, FlexRow, Heading, SearchField, Text} from '@forecast-it/design-system';
import type {ClientImportOption} from './ClientsPage';

// Holds the header off the top of the page. The value lives here rather than on the page wrapper so
// that turning the flag on does not shift the whole block up relative to the pre-redesign layout.
const HeaderWrapper = Styled.div`
  margin-top: 24px;
`;

// The design-system SearchField forwards data-cy to its <input>, which only exists once the field is
// expanded, so the collapsed icon-only state has no hook of its own. This wrapper gives tests and
// Userpilot a selector that is present in both states.
const SearchWrapper = Styled.div`
  display: flex;
`;

type Props = {
	importOptions: ClientImportOption[];
	searchText: string;
	onSearchChange: (value: string) => void;
	onClientPermissions: () => void;
	onImport: (option: ClientImportOption) => void;
	onAddClient: () => void;
};

/**
 * Heading, action buttons and search for the clients list.
 *
 * Presentational: it holds no data of its own and does no fetching, so the search term lives with
 * the list it filters rather than here.
 */
const ClientsPageHeader: React.FC<Props> = ({
	importOptions,
	searchText,
	onSearchChange,
	onClientPermissions,
	onImport,
	onAddClient,
}) => {
	const {formatMessage} = useIntl();

	return (
		<HeaderWrapper>
			<FlexColumn flex="none" gap="xl">
				<FlexRow flex="none" justifyContent="space-between" alignItems="center">
					{/* `as="h1"` is deliberate: `size` is typography only and carries no heading level, so the
					    page title is the document's h1 even though it is rendered at size 3. Without it the
					    design-system Heading renders a div and the page has no heading landmark (IGNITE-621). */}
					<Heading size="3" as="h1" data-cy="clients-page-title">
						{formatMessage({id: 'clients_page.all_clients_heading'})}
					</Heading>
					{/* The accent blue is reserved for the one primary action, so the permissions and import
					    buttons are pinned to mono — they would otherwise default to high emphasis. */}
					<FlexRow justifyContent="end" alignItems="center" gap="s">
						<Button emphasis={EMPHASIS.MONO} data-cy="edit-client-permissions-button" onPress={onClientPermissions}>
							{formatMessage({id: 'clients_page.view_client_permissions'})}
						</Button>
						{importOptions.map(option => (
							<Button
								key={option.modalType}
								emphasis={EMPHASIS.MONO}
								data-cy={`import-from-${option.systemName.toLowerCase()}-button`}
								onPress={() => onImport(option)}
							>
								{formatMessage({id: 'settings_clients.import_from'}, {systemName: option.systemName})}
							</Button>
						))}
						<Button emphasis={EMPHASIS.HIGH} icon="plus" data-cy="add-client-button" onPress={onAddClient}>
							{formatMessage({id: 'clients_page.add_client'})}
						</Button>
					</FlexRow>
				</FlexRow>

				<FlexRow flex="none" justifyContent="space-between" alignItems="center">
					<Text size="1">{formatMessage({id: 'clients_page.description'})}</Text>
					{/* Collapsed to an icon button until pressed, and back again when it loses focus while
					    empty. Controlled, so the list filters as the user types. */}
					<SearchWrapper data-cy="clients-search-field">
						<SearchField
							value={searchText}
							onChange={onSearchChange}
							placeholder={formatMessage({id: 'clients_page.search_placeholder'})}
							data-cy="clients-search"
							emphasis={EMPHASIS.MONO}
						/>
					</SearchWrapper>
				</FlexRow>
			</FlexColumn>
		</HeaderWrapper>
	);
};

export default ClientsPageHeader;
